import { MarketplaceListingCardProps } from '@/components/marketplace/MarketplaceListingCard';
import { extractListingImages } from './listingDataTransform';

/**
 * Normalizes helicopter listing data to MarketplaceListingCard props
 * Extracts operational data: airframe/engine hours & landings/cycles, configuration, equipment
 */
export function normalizeHelicopterForCard(
  listing: any,
  isBuyRequest: boolean = false
): MarketplaceListingCardProps {
  const helicopter = listing.helicopters || listing.helicopter;
  const images = extractListingImages(listing);
  
  // Parse operational_requirements to get aircraftDetails
  let opReqs: any = null;
  let aircraftDetails: any = null;
  
  if (listing.operational_requirements) {
    try {
      opReqs = typeof listing.operational_requirements === 'string'
        ? JSON.parse(listing.operational_requirements)
        : listing.operational_requirements;
      aircraftDetails = opReqs?.aircraftDetails || null;
    } catch (e) {
      console.warn('Error parsing operational_requirements:', e);
    }
  }
  
  // Parse included_services for wet lease
  let includedServices: string[] = [];
  if (listing.included_services) {
    try {
      const services = typeof listing.included_services === 'string'
        ? JSON.parse(listing.included_services)
        : listing.included_services;
      if (Array.isArray(services)) {
        includedServices = services.filter((s: any) => typeof s === 'string');
      }
    } catch (e) {
      console.warn('Error parsing included_services:', e);
    }
  }
  
  // Extract configuration - clean up suffixes like " Configuration"
  let configurationName = listing.configuration_name || listing.configuration_type;
  if (configurationName) {
    configurationName = configurationName.replace(/\s*Configuration$/i, '').trim();
  }
  
  // Extract lease terms for rates
  let leaseTerms: any = null;
  if (listing.lease_terms) {
    try {
      leaseTerms = typeof listing.lease_terms === 'string'
        ? JSON.parse(listing.lease_terms)
        : listing.lease_terms;
    } catch (e) {
      console.warn('Error parsing lease_terms:', e);
    }
  }
  
  // Extract equipment from multiple sources in priority order:
  // 1. selected_options (resolved option names from join)
  // 2. required_equipment
  // 3. operational_requirements.equipment
  // 4. included_services (for wet lease)
  let selectedEquipment: string[] = [];
  
  // Priority 1: selected_options (already resolved names from query join)
  if (listing.selected_options && Array.isArray(listing.selected_options)) {
    selectedEquipment = listing.selected_options
      .map((opt: any) => opt?.name || opt?.option_name || (typeof opt === 'string' ? opt : null))
      .filter(Boolean);
  }
  
  // Priority 2: required_equipment JSON
  if (selectedEquipment.length === 0 && listing.required_equipment) {
    try {
      const equipment = typeof listing.required_equipment === 'string' 
        ? JSON.parse(listing.required_equipment) 
        : listing.required_equipment;
      if (Array.isArray(equipment)) {
        selectedEquipment = equipment.map((e: any) => 
          typeof e === 'string' ? e : e.name || e.label || String(e)
        ).filter(Boolean);
      }
    } catch (e) {
      console.warn('Error parsing required_equipment:', e);
    }
  }
  
  // Priority 3: operational_requirements.equipment or selectedOptions
  if (selectedEquipment.length === 0 && opReqs) {
    const equipmentSource = opReqs.equipment || opReqs.selectedOptions || [];
    if (Array.isArray(equipmentSource)) {
      selectedEquipment = equipmentSource.map((e: any) =>
        typeof e === 'string' ? e : e.name || e.label || String(e)
      ).filter(Boolean);
    }
  }
  
  // Priority 4: included_services for wet lease if no other equipment
  if (selectedEquipment.length === 0 && includedServices.length > 0) {
    selectedEquipment = includedServices;
  }
  
  // Extract operational data - prefer top-level columns, fallback to aircraftDetails
  const airframeHours = listing.total_flight_hours ?? 
    aircraftDetails?.totalTimeSinceNew ?? 
    aircraftDetails?.totalFlightHours ?? 
    aircraftDetails?.airframeHours;
    
  // Airframe uses LANDINGS not cycles
  const airframeLandings = aircraftDetails?.totalLandings ?? 
    aircraftDetails?.airframeLandings ?? 
    null;
    
  const engineHours = listing.engine_current_hours ?? 
    aircraftDetails?.engineCurrentHours ?? 
    aircraftDetails?.engineHours;
    
  const engineCycles = listing.engine_cycles ?? 
    aircraftDetails?.engineCycles;
    
  const engineLifeRemaining = listing.engine_life_remaining ?? 
    aircraftDetails?.engineLifeRemaining;
    
  const engineLifeLimit = listing.engine_life_limit ?? 
    aircraftDetails?.engineLifeLimit;
  
  // Determine if wet lease includes pilot/crew
  const pilotProvided = listing.listing_type === 'wet_lease' || 
    includedServices.some((s: string) => 
      s.toLowerCase().includes('pilot') || 
      s.toLowerCase().includes('crew')
    );
  
  // Calculate the display price based on rate priority: hourly > daily > monthly > budget
  let displayPrice = listing.price_usd || listing.budget_max;
  let rentalPeriod: string | undefined;
  
  // For buy dry/wet lease, check for rate options
  if (isBuyRequest && leaseTerms) {
    if (leaseTerms.hourlyRate) {
      displayPrice = leaseTerms.hourlyRate;
      rentalPeriod = 'hour';
    } else if (leaseTerms.dailyRate) {
      displayPrice = leaseTerms.dailyRate;
      rentalPeriod = 'day';
    } else if (leaseTerms.monthlyRate) {
      displayPrice = leaseTerms.monthlyRate;
      rentalPeriod = 'month';
    }
  }
  
  return {
    id: listing.id,
    title: listing.title || `${helicopter?.manufacturer || ''} ${helicopter?.model || ''}`.trim(),
    price: displayPrice,
    location: listing.location,
    latitude: listing.latitude,
    longitude: listing.longitude,
    images,
    featured: listing.featured || false,
    listingType: (() => {
      // Compute correct listing type for badges - buy requests need special handling
      if (listing.is_buy_request || isBuyRequest) {
        if (listing.lease_type === 'dry_lease') return 'buy_dry_lease';
        if (listing.lease_type === 'wet_lease') return 'buy_wet_lease';
        return 'buy_sale';
      }
      return listing.listing_type || 'sale';
    })(),
    assetType: 'helicopter',
    isBuyRequest: listing.is_buy_request || isBuyRequest,
    condition: listing.condition,
    year: listing.year_manufactured,
    sellerId: listing.user_id,
    sellerName: listing.profiles?.display_name || listing.seller_name,
    listingStatus: listing.status,
    // Pass through visibility settings for privacy controls
    visibilitySettings: listing.visibility_settings,
    helicopterData: {
      manufacturer: helicopter?.manufacturer,
      model: helicopter?.model,
      // Operational data - buyer critical (extracted from aircraftDetails)
      airframeHours,
      airframeLandings, // NEW: landings instead of cycles
      engineHours,
      engineCycles,
      engineLifeRemaining,
      engineLifeLimit,
      mainRotorHours: listing.main_rotor_current_hours ?? aircraftDetails?.mainRotorHours,
      mainRotorLifeLimit: listing.main_rotor_life_limit ?? aircraftDetails?.mainRotorLifeLimit,
      // Configuration & equipment
      configurationName,
      selectedEquipment: selectedEquipment.length > 0 ? selectedEquipment : undefined,
      // Lease terms
      leaseMinPeriod: listing.minimum_lease_period,
      leaseMaxPeriod: listing.maximum_lease_period,
      // Wet lease specific
      includedServices: includedServices.length > 0 ? includedServices : undefined,
      pilotProvided,
      // Buy request fields
      urgencyLevel: listing.urgency_level,
      financingStatus: listing.financing_status,
      maxAge: listing.max_age,
      maxFlightHours: listing.max_flight_hours,
      // Rate options for display
      rentalPeriod,
      hourlyRate: leaseTerms?.hourlyRate,
      dailyRate: leaseTerms?.dailyRate,
      monthlyRate: leaseTerms?.monthlyRate,
      // Insurance & qualifications
      insuranceCapability: opReqs?.insuranceCapability || opReqs?.insuranceRequirements?.[0],
      pilotQualifications: opReqs?.pilotQualifications,
    },
  };
}

/**
 * Normalizes engine listing data to MarketplaceListingCard props
 * Expands to capture all wizard fields for type-specific displays
 */
export function normalizeEngineForCard(listing: any): MarketplaceListingCardProps {
  // Get images from listing_media or photos array
  const images = listing.media?.map((m: any) => m.file_url) || listing.photos || [];
  
  // Parse certifications - could be array or string
  let certifications: string[] = [];
  if (listing.certifications) {
    certifications = Array.isArray(listing.certifications) 
      ? listing.certifications 
      : typeof listing.certifications === 'string' 
        ? [listing.certifications] 
        : [];
  }
  
  // Determine if it's a buy/rent request
  const isBuyRequest = listing.listing_type?.includes('buy') || listing.listing_type?.includes('request');
  
  // For buy requests, the total_tsn/csn/tso/cso represent MAXIMUM acceptable values
  // For sell/rent, they represent actual engine stats
  const tsn = isBuyRequest ? null : listing.total_tsn;
  const csn = isBuyRequest ? null : listing.total_csn;
  const tso = isBuyRequest ? null : listing.last_overhaul_tso;
  const cso = isBuyRequest ? null : listing.last_overhaul_cso;
  
  // For buy requests, map these as maximum requirements
  const maxTsn = isBuyRequest ? listing.total_tsn : null;
  const maxCsn = isBuyRequest ? listing.total_csn : null;
  const maxTso = isBuyRequest ? listing.last_overhaul_tso : null;
  const maxCso = isBuyRequest ? listing.last_overhaul_cso : null;
  
  return {
    id: listing.id,
    title: listing.title || `${listing.manufacturer || ''} ${listing.model || ''}`.trim(),
    price: listing.price_usd || listing.max_price || listing.rental_rate || listing.max_rate,
    location: [listing.city, listing.country].filter(Boolean).join(', '),
    latitude: listing.latitude,
    longitude: listing.longitude,
    images,
    featured: listing.featured || false,
    listingType: listing.listing_type || 'engine_sell',
    assetType: 'engine',
    isBuyRequest,
    condition: listing.overall_condition,
    sellerId: listing.user_id,
    sellerName: listing.seller_name || listing.profiles?.display_name,
    listingStatus: listing.status,
    // Pass through visibility settings for privacy controls
    visibilitySettings: listing.visibility_settings,
    engineData: {
      // Core specs - only populated for sell/rent
      tsn,
      csn,
      tso,
      cso,
      tbo: listing.recommended_tbo,
      powerOutput: listing.power_output,
      powerUnit: listing.power_unit,
      manufacturer: listing.manufacturer,
      model: listing.model,
      variant: listing.variant,
      serialNumber: listing.serial_number,
      // Rental fields
      rentalRate: listing.rental_rate,
      rentalPeriod: listing.rental_period,
      minimumTerm: listing.minimum_term,
      preferredTerm: listing.preferred_term,
      deposit: listing.deposit,
      maintenanceResponsibility: listing.maintenance_responsibility,
      // Buy request fields - maximum requirements
      maxPrice: listing.max_price,
      maxRate: listing.max_rate,
      maxTsn,
      maxCsn,
      maxTso,
      maxCso,
      // Condition & documentation
      certifications,
      logbooksAvailable: listing.logbooks_available,
      accidentFree: listing.accident_free,
      flexiblePrice: listing.flexible_price,
      // Shipping
      shippingOptions: listing.shipping_options,
    },
  };
}

/**
 * Normalizes parts listing data to MarketplaceListingCard props
 * Expands to capture all wizard fields for type-specific displays
 */
export function normalizePartsForCard(listing: any): MarketplaceListingCardProps {
  // Fix image extraction - hero_image is a string, not array
  const images: string[] = [];
  
  // Priority 1: hero_image (single string)
  if (listing.hero_image && typeof listing.hero_image === 'string') {
    images.push(listing.hero_image);
  }
  
  // Priority 2: photos array
  if (listing.photos && Array.isArray(listing.photos)) {
    listing.photos.forEach((photo: any) => {
      if (photo && typeof photo === 'string' && !images.includes(photo)) {
        images.push(photo);
      }
    });
  }
  
  // Priority 3: listing_media joined data
  if (listing.listing_media && Array.isArray(listing.listing_media)) {
    listing.listing_media.forEach((m: any) => {
      if (m?.file_url && !images.includes(m.file_url)) {
        images.push(m.file_url);
      }
    });
  }
  
  // Determine if it's a buy/rent request
  const isBuyRequest = listing.listing_type?.includes('buy') || listing.listing_type?.includes('request');
  
  // Parse certifications - could be array or string
  let certifications: string[] = [];
  if (listing.certifications) {
    certifications = Array.isArray(listing.certifications) 
      ? listing.certifications 
      : typeof listing.certifications === 'string' 
        ? [listing.certifications] 
        : [];
  }
  
  return {
    id: listing.id,
    title: listing.title || listing.name,
    price: listing.price || listing.max_budget || listing.max_rate || listing.rental_rate,
    location: [listing.city, listing.country].filter(Boolean).join(', '),
    latitude: listing.latitude,
    longitude: listing.longitude,
    images,
    featured: listing.featured || false,
    listingType: listing.listing_type || 'parts_sell',
    assetType: 'parts',
    isBuyRequest,
    condition: listing.condition,
    sellerId: listing.user_id,
    sellerName: listing.seller_name || listing.profiles?.display_name,
    listingStatus: listing.status,
    // Pass through visibility settings for privacy controls
    visibilitySettings: listing.visibility_settings,
    partsData: {
      // Core identity
      itemType: listing.item_type,
      partNumber: listing.part_number,
      serialNumber: listing.serial_number,
      name: listing.name,
      manufacturer: listing.helicopter_manufacturer || listing.engine_manufacturer,
      specificityType: listing.specificity_type,
      helicopterModel: listing.helicopter_model,
      helicopterManufacturer: listing.helicopter_manufacturer,
      helicopterVariant: listing.helicopter_variant,
      engineModel: listing.engine_model,
      engineManufacturer: listing.engine_manufacturer,
      engineVariant: listing.engine_variant,
      engineModule: listing.engine_module,
      
      // Quantity & availability
      quantity: listing.quantity,
      handlingTime: listing.handling_time,
      
      // Time/Cycle data (for parts)
      tsn: listing.tsn,
      csn: listing.csn,
      tso: listing.tso,
      cso: listing.cso,
      lifeLimit: listing.life_limit,
      tbo: listing.tbo,
      
      // Tool-specific
      calibrationStatus: listing.calibration_status,
      calibrationDueDate: listing.calibration_due_date,
      toolType: listing.tool_type,
      
      // Equipment-specific  
      equipmentCategory: listing.equipment_category,
      powerRequirements: listing.power_requirements,
      capacity: listing.capacity,
      operatingWeight: listing.operating_weight,
      material: listing.material,
      
      // Condition & certifications
      certifications,
      condition: listing.condition,
      warranty: listing.warranty,
      description: listing.description,
      
      // Rental fields
      rentalRate: listing.rental_rate,
      rentalPeriod: listing.rental_period,
      minimumTerm: listing.minimum_term,
      preferredTerm: listing.preferred_term,
      deposit: listing.deposit,
      maintenanceTerms: listing.maintenance_terms,
      
      // Buy/Request fields
      maxBudget: listing.max_budget,
      maxRate: listing.max_rate,
      urgency: listing.urgency,
      
      // Shipping
      shippingOptions: listing.shipping_options || [],
      shippingMethods: listing.shipping_methods || [],
      localPickupAvailable: listing.local_pickup_available,
      domesticShipping: listing.domestic_shipping,
      internationalShipping: listing.international_shipping,
      hazmatRequired: listing.hazmat_required,
      specialHandling: listing.special_handling,
    },
  };
}

/**
 * Normalizes job listing data to MarketplaceListingCard props
 * For jobs, we provide enhanced data for the JobListingCard component
 */
export function normalizeJobForCard(listing: any): MarketplaceListingCardProps {
  const isJobPost = listing.listing_type === 'job_post' || listing.listing_type === 'job_contract';
  
  // Extract hero image from listing_media if available
  let heroImage = listing.hero_image;
  if (!heroImage && listing.listing_media && Array.isArray(listing.listing_media) && listing.listing_media.length > 0) {
    // Sort by display_order and get first image
    const sorted = [...listing.listing_media].sort((a, b) => (a.display_order || 0) - (b.display_order || 0));
    heroImage = sorted[0]?.file_url;
  }
  
  // Build images array
  const images: string[] = [];
  if (heroImage) {
    images.push(heroImage);
  } else if (listing.company_logo) {
    images.push(listing.company_logo);
  }
  // Add additional images from listing_media
  if (listing.listing_media && Array.isArray(listing.listing_media)) {
    listing.listing_media.forEach((m: any) => {
      if (m.file_url && !images.includes(m.file_url)) {
        images.push(m.file_url);
      }
    });
  }
  
  return {
    id: listing.id,
    title: listing.title || 'Job Listing',
    price: listing.salary_max || listing.salary_min,
    location: [listing.city, listing.country].filter(Boolean).join(', ') || 'Location TBD',
    latitude: listing.latitude,
    longitude: listing.longitude,
    images: images,
    featured: listing.featured || false,
    listingType: listing.listing_type || 'job_post',
    assetType: 'jobs',
    isBuyRequest: listing.listing_type === 'job_profile' || listing.listing_type === 'job_seeking',
    sellerId: listing.user_id,
    listingStatus: listing.status,
    visibilitySettings: listing.visibility_settings,
    // Job-specific data
    jobData: {
      jobCategory: listing.job_category,
      employmentType: listing.employment_type,
      remoteOptions: listing.remote_options,
      salaryMin: listing.salary_min,
      salaryMax: listing.salary_max,
      salaryType: listing.salary_type,
      companyName: listing.company_name,
      companyLogo: listing.company_logo,
      companyDescription: listing.company_description,
      companyWebsite: listing.company_website,
      currentPosition: listing.current_position,
      experienceYearsMin: listing.experience_years_min,
      experienceYearsMax: listing.experience_years_max,
      flightHoursMin: listing.flight_hours_min,
      licenses: listing.licenses,
      certifications: listing.certifications,
      typeRatings: listing.type_ratings,
      medicalClass: listing.medical_class,
      benefits: listing.benefits,
      requirements: listing.requirements,
      responsibilities: listing.responsibilities,
      relocationOffered: listing.relocation_offered,
      includesBenefits: listing.includes_benefits,
      heroImage: heroImage || listing.company_logo,
    },
  };
}

/**
 * Normalizes service listing data to MarketplaceListingCard props
 * Handles all 4 service wizard types with category-specific data
 * Uses correct field names from service_listings table
 */
export function normalizeServiceForCard(listing: any): MarketplaceListingCardProps {
  // Extract images - prioritize hero_image, then logo, then photos
  const images: string[] = [];
  
  if (listing.hero_image && typeof listing.hero_image === 'string') {
    images.push(listing.hero_image);
  }
  if (listing.company_logo && typeof listing.company_logo === 'string' && !images.includes(listing.company_logo)) {
    images.push(listing.company_logo);
  }
  if (listing.photos && Array.isArray(listing.photos)) {
    listing.photos.forEach((photo: any) => {
      if (photo && typeof photo === 'string' && !images.includes(photo)) {
        images.push(photo);
      }
    });
  }
  if (listing.listing_media && Array.isArray(listing.listing_media)) {
    listing.listing_media.forEach((m: any) => {
      if (m?.file_url && !images.includes(m.file_url)) {
        images.push(m.file_url);
      }
    });
  }

  // Determine if it's a request type
  const isRequest = listing.listing_type === 'service_request' || listing.listing_type === 'rfq_request';

  // Parse regulatory approvals - handle both array and string
  let regulatoryApprovals: string[] = [];
  if (listing.regulatory_approvals) {
    regulatoryApprovals = Array.isArray(listing.regulatory_approvals) 
      ? listing.regulatory_approvals 
      : [];
  }

  // Parse coverage regions
  let coverageRegions: string[] = [];
  if (listing.coverage_regions && Array.isArray(listing.coverage_regions)) {
    coverageRegions = listing.coverage_regions;
  }

  // Parse services offered - use service_types (correct field name)
  let serviceTypes: string[] = [];
  if (listing.service_types && Array.isArray(listing.service_types)) {
    serviceTypes = listing.service_types;
  } else if (listing.services_offered && Array.isArray(listing.services_offered)) {
    serviceTypes = listing.services_offered;
  }

  // Get helicopter and engine capabilities - use correct field names
  const helicopterCapabilityIds = listing.helicopter_capabilities || listing.aircraft_capabilities || [];
  const engineCapabilityIds = listing.engine_capabilities || [];

  return {
    id: listing.id,
    title: listing.title || listing.company_name || 'Service Listing',
    price: listing.base_rate || listing.budget_max,
    location: [listing.city, listing.country].filter(Boolean).join(', ') || undefined,
    latitude: listing.latitude,
    longitude: listing.longitude,
    images,
    featured: listing.featured || false,
    listingType: listing.listing_type || 'service_offer',
    assetType: 'services',
    isBuyRequest: isRequest,
    sellerId: listing.user_id,
    sellerName: listing.company_name,
    listingStatus: listing.status,
    visibilitySettings: listing.visibility_settings,
    // Service-specific data - use correct field names from service_listings table
    servicesData: {
      category: listing.service_category || listing.category, // service_category is the correct field
      listingType: listing.listing_type,
      companyName: listing.company_name,
      companyLogo: listing.company_logo,
      regulatoryApprovals,
      certifications: listing.certifications || [],
      serviceTypes,
      // Pass capability IDs for resolution with icons
      helicopterCapabilityIds: Array.isArray(helicopterCapabilityIds) ? helicopterCapabilityIds : [],
      engineCapabilityIds: Array.isArray(engineCapabilityIds) ? engineCapabilityIds : [],
      // Also pass counts as fallback
      helicopterCapabilities: Array.isArray(helicopterCapabilityIds) ? helicopterCapabilityIds.length : 0,
      engineCapabilities: Array.isArray(engineCapabilityIds) ? engineCapabilityIds.length : 0,
      missionTypes: listing.mission_types || [],
      pricingModel: listing.pricing_model,
      baseRate: listing.base_rate,
      rateUnit: listing.rate_unit || listing.currency,
      responseTime: listing.response_time,
      turnaroundTime: listing.turnaround_time,
      coverageRegions,
      serviceRadius: listing.service_radius,
      aogSupport: listing.aog_support,
      aogResponseTime: listing.aog_response_time,
      shiftCoverage: listing.shift_coverage,
      facilitySize: listing.facility_size_sqm,
      hangarCapacity: listing.hangar_capacity,
      fleetSize: listing.fleet_size,
      instructorCount: listing.instructor_count,
      simulatorTypes: listing.simulator_types || [],
      fuelTypes: listing.fuel_types || [],
      amenities: listing.amenities || [],
      droneTypes: listing.drone_types || [],
      flightAuthorizations: listing.flight_authorizations || [],
    },
  };
}

/**
 * Universal normalizer that routes to the correct normalizer based on asset type
 */
export function normalizeListingForCard(
  listing: any,
  assetType: 'helicopter' | 'engine' | 'parts' | 'jobs' | 'services',
  isBuyRequest: boolean = false
): MarketplaceListingCardProps {
  switch (assetType) {
    case 'engine':
      return normalizeEngineForCard(listing);
    case 'parts':
      return normalizePartsForCard(listing);
    case 'jobs':
      return normalizeJobForCard(listing);
    case 'services':
      return normalizeServiceForCard(listing);
    case 'helicopter':
    default:
      return normalizeHelicopterForCard(listing, isBuyRequest);
  }
}
